import { useEffect, useRef, useState } from "react";
import { usePostHog } from "@posthog/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AdultMyTeensBox from "./cards/AdultMyTeensBox.tsx";
import AdultPostedGigsBox from "./cards/AdultPostedGigsBox.tsx";
import BusinessListingsBox from "./cards/BusinessListingsBox.tsx";
import AdultReviewsBox from "./cards/AdultReviewsBox.tsx";
import AdultUpcomingScheduledGigsBox from "./cards/AdultUpcomingScheduledGigsBox.tsx";
import houseOnHillBackground from "../../../assets/dashboard/house_on_hill_corrected.svg";
import { adultDashboardApi } from "../../../lib/adultDashboard";
import {
  captureAdultGigCreated,
  captureBusinessListingDraftSaved,
  captureBusinessListingPublicationCheckoutStarted,
  captureBusinessListingPublished,
  type AdultGigCreateFlowAnalyticsContext,
  type AdultGigCreateFlowEntryPoint,
} from "../../../lib/analytics";
import { businessListingsApi } from "../../../lib/businessListings";
import { gigsApi } from "../../../lib/gigs";
import { mediaApi } from "../../../lib/media";
import type { AdultDashboardSnapshotResponse } from "../../../types/adultDashboardTypes";
import type { BusinessListingResponse, CreateBusinessListingRequest } from "../../../types/businessListingTypes";
import type { CreateGigRequest } from "../../../types/gigTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import DashboardNotificationsPreview from "../../dashboard/DashboardNotificationsPreview";
import CreateGigModal from "../../gigs/creategig";
import CreateBusinessListingModal from "../../business-listings/CreateBusinessListingModal";
import Button from "../../ui/Button/Button";
import Toast, { type ToastState } from "../../ui/Toast/Toast";
import { useAuth } from "../../../hooks/useAuth";
import { isBusinessAccount } from "../../../utils/AuthUtils";

const AdultDashboardView = () => {
  const posthog = usePostHog();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const businessMode = isBusinessAccount(user?.accountType);
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState<AdultDashboardSnapshotResponse | null>(null);
  const [businessListings, setBusinessListings] = useState<BusinessListingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createGigEntryPoint, setCreateGigEntryPoint] = useState<AdultGigCreateFlowEntryPoint>("dashboard_hero");
  const [isSubmittingCreateGig, setIsSubmittingCreateGig] = useState(false);
  const resumedPostJobIntentRef = useRef(false);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("intent") !== "post-job" || resumedPostJobIntentRef.current) {
      return;
    }
    resumedPostJobIntentRef.current = true;
    setCreateGigEntryPoint("dashboard_hero");
    setIsCreateModalOpen(true);
    posthog?.capture("adult_post_job_intent_resumed", { source: "signup_email_verification" });
    navigate("/dashboard", { replace: true });
  }, [location.search, navigate, posthog]);

  const loadSnapshot = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      if (businessMode) {
        setBusinessListings(await businessListingsApi.listMine());
        setSnapshot(null);
        return;
      }
      const response = await adultDashboardApi.getSnapshot();
      setSnapshot(response);
    } catch {
      setErrorMessage("We couldn't load your dashboard right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadDashboardSnapshot = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);
        if (businessMode) {
          const response = await businessListingsApi.listMine();
          if (!mounted) {
            return;
          }
          setBusinessListings(response);
          setSnapshot(null);
        } else {
          const response = await adultDashboardApi.getSnapshot();
          if (!mounted) {
            return;
          }
          setSnapshot(response);
        }
      } catch {
        if (!mounted) {
          return;
        }
        setErrorMessage("We couldn't load your dashboard right now.");
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadDashboardSnapshot();

    return () => {
      mounted = false;
    };
  }, [businessMode]);

  const postedGigs = snapshot?.postedGigs ?? [];
  const upcomingScheduledGigs = snapshot?.upcomingConfirmedGigs ?? [];
  const completedGigs = snapshot?.completedGigs ?? [];
  const reviews = snapshot?.reviews ?? [];
  const myTeens = snapshot?.myTeens ?? [];
  const businessName = user?.businessName?.trim() || null;
  const firstName = user?.firstName?.trim() || null;

  const openCreateGigModal = (entryPoint: AdultGigCreateFlowEntryPoint) => {
    setCreateGigEntryPoint(entryPoint);
    setIsCreateModalOpen(true);
  };

  const handleCreateGig = async (
    payload: CreateGigRequest,
    images: File[],
    analyticsContext?: AdultGigCreateFlowAnalyticsContext,
  ) => {
    setIsSubmittingCreateGig(true);
    setToast(null);

    try {
      const createdGig = await gigsApi.create(payload);
      captureAdultGigCreated(posthog, {
        gig_id: createdGig.id,
        status: payload.status === "DRAFT" ? "DRAFT" : "PUBLISHED",
        source: "adult_dashboard",
        ...(analyticsContext?.flow_id ? { flow_id: analyticsContext.flow_id } : {}),
        ...(analyticsContext?.entry_point ? { entry_point: analyticsContext.entry_point } : {}),
        ...(analyticsContext?.mode ? { mode: analyticsContext.mode } : {}),
        category: payload.category,
        volunteer: Boolean(payload.volunteer),
        schedule_type: payload.scheduleType ?? "FIXED",
        has_images: images.length > 0,
        image_count: images.length,
        teens_required: payload.teensRequired ?? 1,
        poster_payment_amount_cents: payload.paymentAmountCents,
      });

      let failedImageUploads = 0;
      for (const image of images) {
        try {
          await mediaApi.uploadFile(image, "GIG_IMAGE", createdGig.id);
        } catch {
          failedImageUploads += 1;
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["gigs"] });
      await loadSnapshot();
      setIsCreateModalOpen(false);

      if (failedImageUploads === 0) {
        const successMessage =
          payload.status === "DRAFT"
            ? "Gig saved as draft."
            : "Gig created. Navigate to the posted gigs section to view gig.";
        setToast({ tone: "success", message: successMessage });
        return;
      }

      setToast({
        tone: "error",
        message: `Gig created, but ${failedImageUploads} photo${failedImageUploads === 1 ? "" : "s"} failed to upload.`,
      });
    } catch (requestError) {
      throw new Error(apiErrorToMessage(requestError, "Failed to create gig."));
    } finally {
      setIsSubmittingCreateGig(false);
    }
  };

  const handleCreateBusinessListing = async (payload: CreateBusinessListingRequest) => {
    setIsSubmittingCreateGig(true);
    setToast(null);

    try {
      if ((payload.status ?? "PUBLISHED") === "DRAFT") {
        const listing = await businessListingsApi.createDraft(payload);
        captureBusinessListingDraftSaved(posthog, {
          listing_id: listing.id,
          source: "adult_dashboard",
          category: payload.category,
          job_type: payload.jobType,
          pay_type: payload.payType,
          min_pay_amount_cents: payload.minPayAmountCents,
          max_pay_amount_cents: payload.maxPayAmountCents,
        });
        await loadSnapshot();
        setIsCreateModalOpen(false);
        setToast({
          tone: "success",
          message: "Listing saved as draft.",
        });
        return;
      }

      const response = await businessListingsApi.createPublicationCheckoutSession(payload);
      if (!response.checkoutRequired || response.published) {
        captureBusinessListingPublished(posthog, {
          listing_id: response.listingId,
          source: "business_listings_workspace",
        });
        await loadSnapshot();
        setIsCreateModalOpen(false);
        setToast({
          tone: "success",
          message: "Volunteer listing published. It will stay live for 3 months.",
        });
        return;
      }

      if (!response.checkoutUrl) {
        throw new Error("Checkout was not available for this listing.");
      }

      captureBusinessListingPublicationCheckoutStarted(posthog, {
        source: "adult_dashboard",
        category: payload.category,
        job_type: payload.jobType,
        pay_type: payload.payType,
        min_pay_amount_cents: payload.minPayAmountCents,
        max_pay_amount_cents: payload.maxPayAmountCents,
      });
      window.location.assign(response.checkoutUrl);
    } catch (requestError) {
      throw new Error(apiErrorToMessage(requestError, "Failed to create business listing."));
    } finally {
      setIsSubmittingCreateGig(false);
    }
  };

  return (
    <section className="mx-[calc(50%_-_50vw)] mt-2 w-screen space-y-4 px-2 sm:mx-0 sm:mt-4 sm:w-auto sm:space-y-6 sm:px-0 md:mt-5">
      <Toast
        isOpen={toast !== null}
        message={toast?.message ?? ""}
        tone={toast?.tone ?? "info"}
        onClose={() => setToast(null)}
      />
      <div className="space-y-2 sm:space-y-4">
        <section
          className={`relative overflow-hidden rounded-xl border p-3 shadow-sm sm:p-5 ${
            businessMode
              ? "border-myteengig-border bg-white"
              : "border-sky-100 bg-gradient-to-r from-white via-sky-50 to-sky-100"
          }`}
        >
          {!businessMode ? (
            <img
              src={houseOnHillBackground}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute bottom-0 right-0 h-full max-h-56 w-[82%] object-contain object-right-bottom opacity-35 sm:right-4 sm:h-[90%] sm:w-[42%] sm:opacity-90 lg:right-6 lg:w-[38%]"
            />
          ) : null}
          <div
            className={
              businessMode
                ? "relative z-10 grid gap-3 sm:flex sm:items-center sm:justify-between sm:gap-5"
                : "relative z-10 grid gap-3 sm:min-h-40 sm:max-w-[60%] sm:content-center sm:gap-4 lg:max-w-[58%]"
            }
          >
            <div className="min-w-0 text-left">
              {firstName ? <p className="text-sm font-semibold text-myteengig-primary">Hi {firstName}!</p> : null}
              <h2 className="mt-1 text-2xl font-bold leading-8 text-myteengig-text-primary">
                {businessMode ? "Business Dashboard" : "Adult Dashboard"}
              </h2>
              <p className="mt-2 hidden max-w-2xl text-sm leading-6 text-myteengig-text-muted sm:block">
                {businessMode
                  ? "Track your published openings and drafts from one place."
                  : "Keep track of posted gigs, scheduled gigs, completed work, reviews, and your teens."}
              </p>
              {businessMode && businessName ? <p className="mt-2 text-sm font-semibold text-myteengig-text-primary">{businessName}</p> : null}
            </div>
            <Button
              type="button"
              size={businessMode ? "md" : "lg"}
              onClick={() => openCreateGigModal("dashboard_hero")}
              className={businessMode ? "min-h-10 w-full shrink-0 sm:w-auto" : "min-h-12 w-full justify-center text-base sm:w-72"}
            >
              {businessMode ? "Create Job Listing" : "Post a Gig"}
            </Button>
          </div>
        </section>

        <DashboardNotificationsPreview />
      </div>

      {errorMessage ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{errorMessage}</div>
      ) : null}

      <div className="space-y-3 sm:space-y-4">
        <div>
          <h3 className="inline-flex rounded-md border border-myteengig-border bg-myteengig-secondary px-3 py-1 text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">
            {businessMode ? "Listings" : "Gigs"}
          </h3>
        </div>
        {businessMode ? (
          <BusinessListingsBox listings={businessListings} loading={loading} />
        ) : (
          <>
            <div className="grid gap-3 sm:gap-6 lg:grid-cols-2">
              <AdultPostedGigsBox
                postedGigs={postedGigs}
                loading={loading}
                onCreateGigClick={() => openCreateGigModal("dashboard_empty_posted_gigs")}
              />
              <AdultUpcomingScheduledGigsBox upcomingScheduledGigs={upcomingScheduledGigs} loading={loading} />
            </div>
            <AdultReviewsBox reviews={reviews} completedGigs={completedGigs} loading={loading} />
          </>
        )}
      </div>
      {!businessMode ? (
        <div className="space-y-3 sm:space-y-4">
          <div>
            <h3 className="inline-flex rounded-md border border-myteengig-border bg-myteengig-secondary px-3 py-1 text-xs font-semibold uppercase tracking-wide text-myteengig-text-primary">
              Parent/Guardian
            </h3>
          </div>
          <AdultMyTeensBox myTeens={myTeens} loading={loading} />
        </div>
      ) : null}

      {businessMode ? (
        <CreateBusinessListingModal
          isOpen={isCreateModalOpen}
          isSubmitting={isSubmittingCreateGig}
          onClose={() => {
            if (!isSubmittingCreateGig) {
              setIsCreateModalOpen(false);
            }
          }}
          onSubmit={handleCreateBusinessListing}
        />
      ) : (
        <CreateGigModal
          isOpen={isCreateModalOpen}
          isSubmitting={isSubmittingCreateGig}
          onClose={() => {
            if (!isSubmittingCreateGig) {
              setIsCreateModalOpen(false);
            }
          }}
          onSubmit={handleCreateGig}
          analyticsSource="adult_dashboard"
          analyticsEntryPoint={createGigEntryPoint}
          analyticsMode="create"
        />
      )}
    </section>
  );
};

export default AdultDashboardView;
